import { useCallback, useContext, useMemo } from 'react';

import withoutNulls from 'api/expression/withoutNulls';
import { DashboardFiltersContext } from 'contextProviders/DashboardFiltersProvider';
import AccountPickerContext from 'contexts/AccountPickerContext';
import DashboardsContext from 'contexts/DashboardsContext';
import STORE from 'store';

import { FilterBarSettings } from '../Report/filterBarSettings';

// The dashboard's quick filter and pinned filter settings, saved on the
// dashboard. Undefined outside a dashboard's filter provider.
const useDashboardFilterBarSettings = (
  dashboard: DashboardType | undefined,
): FilterBarSettings | undefined => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const dashboardFilters = useContext(DashboardFiltersContext);

  const save = useCallback(
    async (patch: {
      quickFilterOrder?: string[];
      pinnedFilterFields?: ReportPinnedFilters.Field[];
    }) => {
      if (!dashboard) {
        return;
      }

      // Only the fields changed, so it doesn't undo edits made elsewhere
      await STORE.getDashboardsRef({ accountId: selectedAccountId })
        .doc(dashboard.id)
        .update(withoutNulls(patch));
      addOrUpdateDashboard({ ...dashboard, ...patch });
    },
    [addOrUpdateDashboard, dashboard, selectedAccountId],
  );

  return useMemo(
    () =>
      dashboard && dashboardFilters
        ? {
            quickFilterSource: `dashboard: ${dashboard.id}`,
            quickFilters: dashboardFilters.quickFilters,
            quickFilterOrder: dashboard.quickFilterOrder || window.emptyArray,
            pinnedFilterFields:
              dashboard.pinnedFilterFields || window.emptyArray,
            save,
          }
        : undefined,
    [dashboard, dashboardFilters, save],
  );
};

export default useDashboardFilterBarSettings;
